import React from 'react';

// SegmentedControl — an inset pill group for picking exactly one of 2–4 options.
//
// Use this instead of a row of `outline-*` Buttons whenever the choices are mutually exclusive
// (view switchers, mode toggles, scopes). A row of outline buttons reads as several independent
// actions; a segmented control reads as one setting with a current value, and it carries the
// pressed state accessibly via `aria-pressed`.
//
// Responsive: segments share the width evenly and stretch on phones (`tw-flex-1`), so the control
// becomes a full-width row rather than wrapping raggedly; from `sm` up it sizes to its content.
export interface SegmentedOption<T extends string> {
  value: T;
  label: string;
  /** Bootstrap icon class, e.g. 'bi-table'. Optional but recommended. */
  icon?: string;
  /** Hide the text label below `sm`, keeping just the icon (for space-tight toolbars). */
  iconOnlyOnMobile?: boolean;
  disabled?: boolean;
}

export interface SegmentedControlProps<T extends string> {
  options: SegmentedOption<T>[];
  value: T;
  onChange: (value: T) => void;
  /** Required — this is a group of controls and needs a name for screen readers. */
  'aria-label': string;
  /** Stretch to fill its container instead of sizing to content. */
  block?: boolean;
  className?: string;
}

export function SegmentedControl<T extends string>({
  options,
  value,
  onChange,
  'aria-label': ariaLabel,
  block = false,
  className = '',
}: SegmentedControlProps<T>) {
  return (
    <div
      role="group"
      aria-label={ariaLabel}
      className={`${block ? 'tw-flex tw-w-full' : 'tw-inline-flex'} tw-rounded-lg tw-border tw-border-line-input tw-bg-surface-alt tw-p-0.5 ${className}`}
    >
      {options.map((opt) => {
        const selected = opt.value === value;
        return (
          <button
            key={opt.value}
            type="button"
            aria-pressed={selected}
            disabled={opt.disabled}
            title={opt.label}
            onClick={() => onChange(opt.value)}
            className={`tw-inline-flex tw-min-h-[2.25rem] tw-flex-1 tw-items-center tw-justify-center tw-gap-1.5 tw-rounded-md tw-px-3 tw-text-sm tw-font-medium tw-transition-colors disabled:tw-opacity-50 sm:tw-flex-none ${
              selected
                ? 'tw-bg-surface tw-text-primary tw-shadow-sm'
                : 'tw-text-content-secondary hover:tw-text-content'
            }`}
          >
            {opt.icon && <i className={`bi ${opt.icon}`} aria-hidden="true" />}
            <span className={opt.iconOnlyOnMobile ? 'tw-hidden sm:tw-inline' : ''}>{opt.label}</span>
          </button>
        );
      })}
    </div>
  );
}

export default SegmentedControl;
